/**
 * LLM Usage MCP tools — STR-23 (MCP before UI)
 * Chain: DEC-61, TEN-65
 *
 * AI agents can query their own workspace spend, model breakdown,
 * and session economics before the Settings UI ships.
 */
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import { success, failureResult } from "../envelope.js";
import { kernelQuery, getWorkspaceContext, getAgentSessionId } from "../client.js";

export const usageSummarySchema = z.object({
  periodDays: z
    .number()
    .min(1)
    .max(90)
    .optional()
    .describe("Number of days to look back (default 30, max 90)"),
});

/**
 * WP-484 S2 (§3): extracted usage-summary handler — reused by the legacy
 * `get-usage-summary` standalone tool AND `workspace action=usage` (absorbed
 * per §3 core-nouns table). One business-logic path.
 */
export async function handleUsageSummary(periodDays?: number) {
  const ws = await getWorkspaceContext();
  const sessionId = getAgentSessionId();

  const summary = await kernelQuery<GatewayRouteReturnByName<"usage.getWorkspaceSummary">>("usage.getWorkspaceSummary", {
    periodDays: periodDays ?? 30,
  });

  if (!summary) {
    return failureResult(
      "No usage data available yet. LLM tracking may not be active or no calls have been made in this period.",
      "NOT_FOUND",
      "No usage data available yet.",
      "LLM tracking may not be active or no calls have been made in this period.",
    );
  }

  const lines: string[] = [
    `## Usage Summary — ${ws.workspaceName}`,
    `Period: last ${summary.periodDays} days`,
    "",
    `**Total spend:** $${summary.totalCostUsd.toFixed(4)} USD (${summary.totalRequests} requests)`,
    `**Total tokens:** ${summary.totalTokens.toLocaleString()} (cached: ${summary.cachedTokens.toLocaleString()}, reasoning: ${summary.reasoningTokens.toLocaleString()})`,
    `**Cache hit rate:** ${(summary.cacheHitRate * 100).toFixed(1)}%`,
  ];

  if (summary.modelBreakdown.length > 0) {
    lines.push("", "### Model Breakdown");
    for (const m of summary.modelBreakdown) {
      lines.push(`- **${m.model}**: $${m.costUsd.toFixed(4)} (${m.requests} requests)`);
    }
  }

  if (summary.featureBreakdown.length > 0) {
    lines.push("", "### Feature Breakdown");
    for (const f of summary.featureBreakdown) {
      lines.push(`- **${f.feature}**: $${f.costUsd.toFixed(4)} (${f.requests} requests)`);
    }
  }

  if (sessionId) {
    lines.push("", `_Current agent session: ${sessionId}_`);
  }

  return {
    content: [
      {
        type: "text" as const,
        text: lines.join("\n"),
      },
    ],
    structuredContent: success(
      `Usage for ${ws.workspaceName}: $${summary.totalCostUsd.toFixed(4)} over ${summary.periodDays} days, ${summary.totalRequests} requests.`,
      {
        periodDays: summary.periodDays,
        totalTokens: summary.totalTokens,
        totalCostUsd: summary.totalCostUsd,
        totalRequests: summary.totalRequests,
        cacheHitRate: summary.cacheHitRate,
        modelBreakdown: summary.modelBreakdown,
        featureBreakdown: summary.featureBreakdown,
      },
    ),
  };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `get-usage-summary` tool
// retired here. handleUsageSummary stays live — workspace.ts (the `workspace`
// compound, action=usage) is its only remaining caller.
